import {mapToMaterialsById, useMaterials} from '@domain/materials'
import {useStockDevelopmentQuery} from '@domain/schedule'
import {dataTestId} from '@hconnect/uikit'
import {useUpmPlantTimezone, useUpmPlantContext} from '@hconnect/upm'
import {BoxProps, Collapse, Skeleton} from '@mui/material'
import {
  MomentRange,
  usePlanningChartScheduleData,
  usePlanningChartStartEnd
} from '@widgets/planning-schedule-chart'
import moment from 'moment-timezone'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {usePersistingSettingState} from '../../hooks'
import {PlannerLSSettingsKeys} from '../../types'
import {getLocalizedDayMonthTimeFormat} from '../../utils'
import {CollapsibleCard} from '../CollapsibleCard'

import {OptimizerStockLevelsCardContent} from './OptimizerStockLevelsCardContent'
import {OptimizerStockLevelsCardHeader} from './OptimizerStockLevelsCardHeader'
import {SelectedMaterialIdProvider, useSelectedMaterialId} from './SelectedMaterialIdProvider'

export const OptimizerStockLevelsCardSubtitle = () => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const timezoneId = useUpmPlantTimezone()

  const {materialId} = useSelectedMaterialId()

  const {startOfPlan, endOfPlan} = usePlanningChartStartEnd()
  const planRange = [startOfPlan, endOfPlan] as MomentRange
  const {scheduleId} = usePlanningChartScheduleData()

  const {data: stockDevelopment, dataUpdatedAt} = useStockDevelopmentQuery({
    materialIds: materialId !== undefined ? [materialId] : [],
    scheduleId,
    timeframe: planRange
  })

  if (materialId === undefined) return null

  if (!stockDevelopment) {
    return <Skeleton variant="rectangular" />
  }

  return (
    <>
      {t('common.lastUpdate', {
        date: getLocalizedDayMonthTimeFormat(moment.utc(dataUpdatedAt), timezoneId, language)
      })}
    </>
  )
}

export const OptimizerStockLevelsCardTitle = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const {plantId: plantCode} = useUpmPlantContext()
  const {data: materialsById} = useMaterials({
    plantCode,
    options: {
      select: mapToMaterialsById
    }
  })

  const {materialId} = useSelectedMaterialId()
  if (materialId === undefined) {
    return <>{t('optimizer.stockLevels')}</>
  }

  if (!materialsById) {
    return <Skeleton variant="rectangular" />
  }
  return <>{materialsById[materialId]?.name}</>
}

export const OptimizerStockLevelsCard = (props: BoxProps & {plantCode: string}) => {
  const {plantCode, ...restBoxProps} = props
  const [{isCardOpen}, setSettings] = usePersistingSettingState(
    PlannerLSSettingsKeys.OptimizerPageSettings,
    {isCardOpen: true}
  )

  const toggleIsMinimized = useCallback(() => {
    setSettings({isCardOpen: !isCardOpen})
  }, [setSettings, isCardOpen])

  return (
    <SelectedMaterialIdProvider>
      <CollapsibleCard
        {...restBoxProps}
        isCollapsed={!isCardOpen}
        onToggleCollapsed={toggleIsMinimized}
        title={<OptimizerStockLevelsCardTitle />}
        subtitle={<OptimizerStockLevelsCardSubtitle />}
        headerContent={<OptimizerStockLevelsCardHeader isCollapsed={!isCardOpen} />}
        {...dataTestId('optimizer_stock_levels_card')}
      >
        <Collapse in={isCardOpen}>
          <OptimizerStockLevelsCardContent plantCode={plantCode} />
        </Collapse>
      </CollapsibleCard>
    </SelectedMaterialIdProvider>
  )
}
